/**
 * How a recurrence rule reads to a person: "Fridays", "2nd & 4th Sunday".
 *
 * Shared by the listing and by the change emails, which is the whole point —
 * these were written twice, and the moment one changed its wording the email
 * would have started disagreeing with the page it describes.
 *
 * Pure: no Convex or React imports, so both sides can use it.
 */

export type Recurrence = {
  frequency: string;
  interval?: number;
  dayOfWeek?: number;
  dayOfMonth?: number;
  ordinals?: number[];
  /** Superseded by `ordinals`; still read so older patterns keep working. */
  ordinal?: number;
};

export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Weeks of the month, counted from the start.
 *
 * There is no "last": social dances are scheduled as first through fourth, and
 * a month with a fifth Friday just means a longer wait between them. A legacy
 * -1 reads as the 4th, which is what those organizers actually publish.
 */
export const ORDINAL_NAMES: Record<number, string> = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
};

export const LAST_MEANS_FOURTH = 4;

/** Every ordinal a rule carries, with a legacy -1 resolved and duplicates gone. */
export function ruleOrdinals(rule: Recurrence): number[] {
  const raw = rule.ordinals?.length
    ? rule.ordinals
    : rule.ordinal !== undefined
      ? [rule.ordinal]
      : [];
  return [...new Set(raw.map((o) => (o === -1 ? LAST_MEANS_FOURTH : o)))].sort(
    (a, b) => a - b
  );
}

/** "1st", "2nd & 4th", "1st, 2nd & 4th" */
export function joinOrdinals(ordinals: number[]): string {
  const names = [...new Set(ordinals.map((n) => (n === -1 ? LAST_MEANS_FOURTH : n)))]
    .sort((a, b) => a - b)
    .map((n) => ORDINAL_NAMES[n] ?? `${n}th`);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

function daySuffix(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return "th";
  return ["th", "st", "nd", "rd"][day % 10] ?? "th";
}

/**
 * The rule as a label, or null when the rule is too incomplete to describe —
 * callers fall back to reading the dates themselves.
 *
 * `plural` picks the listing's voice ("Fridays") over the email's, which reads
 * better as a rule being stated ("Every Friday").
 */
export function describeRule(
  rule: Recurrence,
  { plural = true }: { plural?: boolean } = {}
): string | null {
  const n = rule.interval ?? 1;
  const day = rule.dayOfWeek !== undefined ? DAY_NAMES[rule.dayOfWeek] : undefined;

  switch (rule.frequency) {
    case "weekly":
      if (!day) return null;
      if (n === 1) return plural ? `${day}s` : `Every ${day}`;
      if (n === 2) return `Every other ${day}`;
      return `Every ${n} weeks`;

    case "biweekly":
      return day ? `Every other ${day}` : null;

    case "first_weekday_of_month":
      return day ? `1st ${day}` : null;

    // Retired: kept only so patterns predating its removal still describe.
    case "last_weekday_of_month":
      return day ? `${ORDINAL_NAMES[LAST_MEANS_FOURTH]} ${day}` : null;

    case "nth_weekday_of_month": {
      const ordinals = ruleOrdinals(rule);
      if (!day || ordinals.length === 0) return null;
      const label = `${joinOrdinals(ordinals)} ${day}`;
      // Monthly-but-not-every-month is rare enough to spell out rather than
      // let the label quietly overstate how often the dance runs.
      return n > 1 ? `${label}, every ${n} months` : label;
    }

    case "monthly":
      return rule.dayOfMonth !== undefined
        ? `${rule.dayOfMonth}${daySuffix(rule.dayOfMonth)} of the month`
        : null;

    default:
      return null;
  }
}
